import React, { useState, useEffect } from 'react';
import { Modal } from '../core';
import { Button } from '../core/Button';
import type { UserWorkSchedule } from '../../graphql/types/user';

const DAYS: { key: keyof UserWorkSchedule; label: string }[] = [
  { key: 'monday_hours',    label: 'Montag' },
  { key: 'tuesday_hours',   label: 'Dienstag' },
  { key: 'wednesday_hours', label: 'Mittwoch' },
  { key: 'thursday_hours',  label: 'Donnerstag' },
  { key: 'friday_hours',    label: 'Freitag' },
  { key: 'saturday_hours',  label: 'Samstag' },
  { key: 'sunday_hours',    label: 'Sonntag' },
];

const DEFAULT_SCHEDULE: UserWorkSchedule = {
  monday_hours: 8,
  tuesday_hours: 8,
  wednesday_hours: 8,
  thursday_hours: 8,
  friday_hours: 8,
  saturday_hours: 0,
  sunday_hours: 0,
};

interface Props {
  isOpen: boolean;
  initialSchedule?: UserWorkSchedule | null;
  onClose: () => void;
  onSave: (schedule: UserWorkSchedule) => void;
}

export function WorkScheduleModal({ isOpen, initialSchedule, onClose, onSave }: Props) {
  const [raw, setRaw] = useState<Record<keyof UserWorkSchedule, string>>(
    () => toRawStrings(initialSchedule ?? DEFAULT_SCHEDULE)
  );
  const [errors, setErrors] = useState<Partial<Record<keyof UserWorkSchedule, string>>>({});

  useEffect(() => {
    if (isOpen) {
      setRaw(toRawStrings(initialSchedule ?? DEFAULT_SCHEDULE));
      setErrors({});
    }
  }, [isOpen, initialSchedule]);

  function toRawStrings(s: UserWorkSchedule): Record<keyof UserWorkSchedule, string> {
    return Object.fromEntries(
      Object.entries(s).map(([k, v]) => [k, v === 0 ? '' : String(v).replace('.', ',')])
    ) as Record<keyof UserWorkSchedule, string>;
  }

  const handleChange = (key: keyof UserWorkSchedule, value: string) => {
    if (value !== '' && !/^[0-9]*[.,]?[0-9]*$/.test(value)) return;
    setRaw((prev) => ({ ...prev, [key]: value }));
    setErrors((prev) => ({ ...prev, [key]: '' }));
  };

  const parseSchedule = (): UserWorkSchedule | null => {
    const result = {} as UserWorkSchedule;
    const next: Partial<Record<keyof UserWorkSchedule, string>> = {};

    for (const { key } of DAYS) {
      const num = parseFloat((raw[key] || '0').replace(',', '.'));
      if (isNaN(num) || num < 0 || num > 24) {
        next[key] = 'Must be between 0 and 24';
      } else {
        result[key] = num;
      }
    }

    if (Object.keys(next).length > 0) {
      setErrors(next);
      return null;
    }

    return result;
  };

  const weeklyTotal = DAYS.reduce((sum, { key }) => {
    const num = parseFloat((raw[key] || '0').replace(',', '.'));
    return sum + (isNaN(num) ? 0 : num);
  }, 0);

  const handleSave = () => {
    const schedule = parseSchedule();
    if (!schedule) return;
    onSave(schedule);
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} title="Zeitmodel">
      <div className="space-y-3">
        <p className="text-[13px] text-[#6b6b6b]">
          Legen Sie die angestrebte tägliche Arbeitszeit für jeden Wochentag fest.
        </p>

        <div className="space-y-1">
          {DAYS.map(({ key, label }) => (
            <div key={key}>
              <div className="flex items-center gap-3">
                <span className="text-[13px] text-[#1c1c1c] w-24 shrink-0">{label}</span>
                <div className="flex items-center gap-2">
                  <input
                    type="text"
                    inputMode="decimal"
                    value={raw[key]}
                    placeholder="0"
                    onChange={(e) => handleChange(key, e.target.value)}
                    className={`w-16 h-[34px] text-right text-[13px] px-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-[#0084c7] focus:border-transparent ${
                      errors[key] ? 'border-red-400' : 'border-[#e0e0e0]'
                    }`}
                  />
                  <span className="text-[13px] text-[#6b6b6b]">Std</span>
                </div>
              </div>
              {errors[key] && (
                <div className="text-red-500 text-[12px] mt-0.5 ml-28">{errors[key]}</div>
              )}
            </div>
          ))}
        </div>

        <div className="flex justify-between items-center pt-2 border-t border-[#e0e0e0]">
          <span className="text-[13px] font-medium text-[#1c1c1c]">Wöchentliche Gesamtsumme</span>
          <span className="text-[13px] font-semibold text-[#0084c7]">
            {weeklyTotal.toLocaleString('de-DE', { minimumFractionDigits: 0, maximumFractionDigits: 2 })} Std
          </span>
        </div>

        <div className="flex justify-end gap-3 pt-1">
          <Button variant="secondary" onClick={onClose}>Abbrechen</Button>
          <Button variant="primary" onClick={handleSave}>Speichern</Button>
        </div>
      </div>
    </Modal>
  );
}